import { z } from "zod";
import { pageOf, pageQuerySchema } from "../common/pagination.js";
import { patchOf } from "../common/patch.js";
import { localDateSchema, moneySchema, quantitySchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
/**
 * Akaryakıt siparişi (Prompt 12 §3): dağıtıcıya verilen ürün bazlı miktar talebi.
 *
 * Sipariş bir SÖZ'dür, stok hareketi değildir: muhasebe kaydı üretmez. Stoğa giriş, dolumun (delivery) onaylanmasıyla
 * ve maliyetin alış faturasından okunmasıyla olur. Onay motoru belge türü `FUEL_ORDER`'dır.
 */
export const FUEL_ORDER_STATUSES = [
    "DRAFT",
    "PENDING_APPROVAL",
    "APPROVED",
    "SENT",
    "DELIVERED",
    "REJECTED",
    "CANCELLED",
];
export const fuelOrderStatusSchema = z.enum(FUEL_ORDER_STATUSES);
export const FUEL_ORDER_STATUS_LABELS = {
    DRAFT: "Taslak",
    PENDING_APPROVAL: "Onay bekliyor",
    APPROVED: "Onaylandı",
    SENT: "Dağıtıcıya iletildi",
    DELIVERED: "Teslim alındı",
    REJECTED: "Reddedildi",
    CANCELLED: "İptal edildi",
};
/** Sipariş anındaki DBS limit özeti — UYARIDIR, siparişi engellemez; belgede dondurulur. */
export const fuelOrderDbsWarningSchema = z.object({
    counterpartyId: uuidSchema,
    hasAgreement: z.boolean(),
    limitAmount: moneySchema,
    usedAmount: moneySchema,
    availableAmount: moneySchema,
    /** Tahmini tutar kullanılabilir limiti aşıyor mu? Tahmini tutar yoksa null. */
    exceedsLimit: z.boolean().nullable(),
    checkedAt: timestampSchema,
});
export const fuelOrderLineInputSchema = z.object({
    productId: uuidSchema,
    quantityL: quantitySchema.refine((value) => Number(value) > 0, "Miktar sıfırdan büyük olmalıdır"),
});
const orderLinesInput = z
    .array(fuelOrderLineInputSchema)
    .min(1)
    .max(20)
    .refine((lines) => new Set(lines.map((line) => line.productId)).size === lines.length, "Aynı ürün iki satırda olamaz");
export const createFuelOrderSchema = z.object({
    companyId: uuidSchema,
    stationId: uuidSchema,
    distributorCounterpartyId: uuidSchema.describe("Dağıtıcı carisi (tedarikçi)"),
    requestedDeliveryDate: localDateSchema,
    note: z.string().trim().max(1000).nullable().optional(),
    lines: orderLinesInput,
});
/** PATCH: yalnızca TASLAK siparişte; gönderilen `lines` satırların TAMAMINI değiştirir. */
export const updateFuelOrderSchema = patchOf(createFuelOrderSchema.omit({ companyId: true, stationId: true })).extend({ version: z.number().int().min(1) });
export const fuelOrderLineSchema = z.object({
    id: uuidSchema,
    lineNo: z.number().int(),
    productId: uuidSchema,
    productName: z.string(),
    quantityL: quantitySchema,
    deliveredL: quantitySchema,
});
export const fuelOrderSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    stationId: uuidSchema,
    orderNo: z.string().describe("SP{yıl}{6 hane} — boşluksuz, şirket + yıl bazında"),
    distributorCounterpartyId: uuidSchema,
    distributorName: z.string(),
    requestedDeliveryDate: localDateSchema,
    status: fuelOrderStatusSchema,
    /** Σ miktar × kapsamın son ortalama maliyeti; maliyet hiç oluşmadıysa null. */
    estimatedAmount: moneySchema.nullable(),
    dbsWarning: fuelOrderDbsWarningSchema.nullable(),
    approvalRequestId: uuidSchema.nullable(),
    note: z.string().nullable(),
    sentAt: timestampSchema.nullable(),
    cancelledAt: timestampSchema.nullable(),
    cancelReason: z.string().nullable(),
    createdBy: uuidSchema.nullable(),
    createdAt: timestampSchema,
    version: z.number().int(),
    lines: z.array(fuelOrderLineSchema),
});
export const listFuelOrdersQuerySchema = pageQuerySchema.extend({
    companyId: uuidSchema,
    stationId: uuidSchema.optional(),
    status: fuelOrderStatusSchema.optional(),
    distributorCounterpartyId: uuidSchema.optional(),
    from: localDateSchema.optional(),
    to: localDateSchema.optional(),
});
export const fuelOrderPageSchema = pageOf(fuelOrderSchema);
const versionOnly = { version: z.number().int().min(1) };
export const submitFuelOrderSchema = z.object(versionOnly);
export const markFuelOrderSentSchema = z.object({
    ...versionOnly,
    sentAt: timestampSchema.optional(),
});
export const cancelFuelOrderSchema = z.object({
    ...versionOnly,
    reason: z.string().trim().min(3).max(500),
});
export const fuelOrderParamsSchema = z.object({ id: uuidSchema });
//# sourceMappingURL=orders.js.map